/**
 * Client cho "Domain Check Logger API" — một service HOÀN TOÀN RIÊNG so với gateway
 * chính (`src/lib/api.ts`), xem `Logs.md` (không phải `API.md`) làm nguồn chuẩn.
 * Khác biệt quan trọng:
 *
 * - Auth bằng `X-Api-Key` + `X-Api-Secret` tĩnh, KHÔNG phải bearer token của user
 *   đang đăng nhập — key được cấp phát ngoài luồng, nhúng qua biến môi trường build.
 * - Chỉ dùng `GET /v1/logs` (đọc) ở CMS này; không ingest log từ đây.
 *
 * ⚠️ App này build thành bundle tĩnh chạy hẳn trên trình duyệt — mọi giá trị inline ở
 * đây (kể cả secret) đều lộ ra với bất kỳ ai mở DevTools. Chỉ nhúng key có quyền hạn
 * đã được chấp nhận rủi ro này (khuyến nghị: key theo brand, KHÔNG dùng master key
 * `brand: "*"` trừ khi thực sự cần xem chéo brand).
 */
import type { DomainCheckLog, DomainCheckLogQuery, DomainCheckLogsResponse } from "./types";

/** Cùng bẫy try/catch như `envApiBase()` trong `api.ts` — xem comment ở đó. */
function readEnv(name: string): string | undefined {
  try {
    switch (name) {
      case "BUN_PUBLIC_LOGS_API_BASE":
        return process.env.BUN_PUBLIC_LOGS_API_BASE;
      case "BUN_PUBLIC_LOGS_API_KEY":
        return process.env.BUN_PUBLIC_LOGS_API_KEY;
      case "BUN_PUBLIC_LOGS_API_SECRET":
        return process.env.BUN_PUBLIC_LOGS_API_SECRET;
      default:
        return undefined;
    }
  } catch {
    return undefined; // biến chưa đặt lúc build
  }
}

const LOGS_BASE = readEnv("BUN_PUBLIC_LOGS_API_BASE");
const LOGS_KEY = readEnv("BUN_PUBLIC_LOGS_API_KEY");
const LOGS_SECRET = readEnv("BUN_PUBLIC_LOGS_API_SECRET");

/** Chưa cấu hình đủ 3 biến thì coi như tính năng tắt — trang Logs hiện thông báo thay vì gọi API. */
export function logsApiConfigured(): boolean {
  return Boolean(LOGS_BASE && LOGS_KEY && LOGS_SECRET);
}

export class LogsApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "LogsApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!LOGS_BASE || !LOGS_KEY || !LOGS_SECRET) {
    throw new LogsApiError(0, "Domain Check Logger API chưa được cấu hình (thiếu biến môi trường BUN_PUBLIC_LOGS_API_*).");
  }

  const headers = new Headers(init.headers);
  headers.set("X-Api-Key", LOGS_KEY);
  headers.set("X-Api-Secret", LOGS_SECRET);

  let res: Response;
  try {
    res = await fetch(`${LOGS_BASE}${path}`, { ...init, headers });
  } catch {
    throw new LogsApiError(0, "Không kết nối được Domain Check Logger API.");
  }

  if (!res.ok) {
    let detail: string | undefined;
    try {
      const body = (await res.json()) as { error?: string };
      detail = body.error;
    } catch {
      // body không phải JSON — bỏ qua, dùng thông báo theo mã HTTP.
    }
    throw new LogsApiError(res.status, detail ?? `Lỗi ${res.status} từ Domain Check Logger API`);
  }

  return (await res.json()) as T;
}

function buildQuery(query: DomainCheckLogQuery): string {
  const params = new URLSearchParams();
  if (query.brand) params.set("brand", query.brand);
  if (query.domain) params.set("domain", query.domain);
  if (query.ip) params.set("ip", query.ip);
  if (query.location) params.set("location", query.location);
  if (query.result) params.set("result", query.result);
  params.set("limit", String(query.limit ?? 50));
  params.set("offset", String(query.offset ?? 0));
  return params.toString();
}

export const logsApi = {
  /**
   * `GET /v1/logs`. Key thường luôn bị khoá về brand của chính key đó — `brand` trong
   * query chỉ có tác dụng với master key (`brand: "*"`), server tự bỏ qua tham số này
   * với key thường (không phải lỗi, xem `Logs.md`).
   */
  list: (query: DomainCheckLogQuery = {}) =>
    request<DomainCheckLogsResponse>(`/v1/logs?${buildQuery(query)}`),
};

export type { DomainCheckLog, DomainCheckLogQuery, DomainCheckLogsResponse };
